<template>
  <component :is="layout" v-if="layout">
    <router-view />
  </component>
  <router-view v-else />
</template>

<script>
import { computed, defineComponent, h } from 'vue'
import { useRoute } from 'vue-router'
import AuthLayout from '@/layouts/AuthLayout.vue'
import DefaultLayout from '@/layouts/DefaultLayout.vue'

// Пустой layout — рендерит только контент, без сайдбара и хедера. Для kiosk-режимов.
const BlankLayout = defineComponent({
  name: 'BlankLayout',
  setup(_, { slots }) {
    return () => h('div', { class: 'blank-layout' }, slots.default ? slots.default() : [])
  },
})

export default {
  name: 'App',
  components: { AuthLayout, DefaultLayout, BlankLayout },
  setup() {
    const route = useRoute()
    const layout = computed(() => {
      if (route.meta.layout === 'auth') return 'AuthLayout'
      if (route.meta.layout === 'blank') return 'BlankLayout'
      return 'DefaultLayout'
    })
    return { layout }
  },
}
</script>

<style>
/* Базовый шрифт приложения */
@import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&display=swap');

* {
  box-sizing: border-box;
}
:root {
  --sidebar-width: 18vw;
}
body {
  margin: 0;
  color: #030229;
  font-family: 'Inter', sans-serif;
  background-color: #f3f3f5;
}

/* Единый шрифт по всему приложению (перебивает локальные Arial/Times/Manrope в scoped-стилях) */
body, body *:not(code):not(pre):not(kbd):not(samp) {
  font-family: 'Inter', sans-serif !important;
}

h1, h2, h3, h4, h5, h6 {
  color: #030229;
  font-family: 'Inter', sans-serif;
  font-weight: 600;
}

button, input[type="button"], input[type="submit"], [role="button"], 
.btn-primary, .btn-secondary, .btn-primary-modal, .action-btn, 
.close-btn, .link-btn, .filter-btn, .header__sidebar-toggle, 
.primary-item, .menu-group-header, .menu-item, .settings {
  cursor: pointer;
  font-family: 'Inter', sans-serif;
  transition: all 0.2s ease;
}

button:hover:not(:disabled), input[type="button"]:hover, input[type="submit"]:hover, [role="button"]:hover {
  filter: brightness(0.95);
}

input, select, textarea {
  font-family: 'Inter', sans-serif;
}

select, input[type="checkbox"], input[type="radio"], label[for] {
  cursor: pointer;
}

/* UI Utilities */
.cursor-pointer { cursor: pointer; }
.flex { display: flex; }
.flex-col { flex-direction: column; }
.items-center { align-items: center; }
.items-start { align-items: flex-start; }
.items-end { align-items: flex-end; }
.justify-between { justify-content: space-between; }
.justify-center { justify-content: center; }
.flex-1 { flex: 1; }
.flex-wrap { flex-wrap: wrap; }
.gap-1 { gap: 0.25rem; }
.gap-2 { gap: 0.5rem; }
.gap-3 { gap: 0.75rem; }
.gap-4 { gap: 1rem; }
.gap-6 { gap: 1.5rem; }
.gap-8 { gap: 2rem; }
.gap-x-8 { column-gap: 2rem; }
.gap-y-3 { row-gap: 0.75rem; }
.gap-y-1\.5 { row-gap: 0.375rem; }

.grid { display: grid; }
.grid-cols-1 { grid-template-columns: repeat(1, minmax(0, 1fr)); }
.grid-cols-2 { grid-template-columns: repeat(2, minmax(0, 1fr)); }

@media (min-width: 640px) {
  .sm\:grid-cols-2 { grid-template-columns: repeat(2, minmax(0, 1fr)); }
}

@media (min-width: 768px) {
  .md\:grid-cols-3 { grid-template-columns: repeat(3, minmax(0, 1fr)); }
  .md\:grid-cols-2 { grid-template-columns: repeat(2, minmax(0, 1fr)); }
}

@media (min-width: 1024px) {
  .lg\:grid-cols-3 { grid-template-columns: repeat(3, minmax(0, 1fr)); }
  .lg\:grid-cols-4 { grid-template-columns: repeat(4, minmax(0, 1fr)); }
  .lg\:grid-cols-2 { grid-template-columns: repeat(2, minmax(0, 1fr)); }
}

.m-0 { margin: 0; }
.mb-1 { margin-bottom: 0.25rem; }
.mb-2 { margin-bottom: 0.5rem; }
.mb-3 { margin-bottom: 0.75rem; }
.mb-4 { margin-bottom: 1rem; }
.mb-6 { margin-bottom: 1.5rem; }
.mb-8 { margin-bottom: 2rem; }
.mt-2 { margin-top: 0.5rem; }
.mt-3 { margin-top: 0.75rem; }
.mt-4 { margin-top: 1rem; }
.mt-6 { margin-top: 1.5rem; }
.mr-1 { margin-right: 0.25rem; }
.mr-2 { margin-right: 0.5rem; }
.ml-1 { margin-left: 0.25rem; }
.p-2\.5 { padding: 0.625rem; }
.p-3 { padding: 0.75rem; }
.p-4 { padding: 1rem; }
.p-5 { padding: 1.25rem; }
.p-6 { padding: 1.5rem; }
.p-8 { padding: 2rem; }
.px-2 { padding-left: 0.5rem; padding-right: 0.5rem; }
.px-3 { padding-left: 0.75rem; padding-right: 0.75rem; }
.px-4 { padding-left: 1rem; padding-right: 1rem; }
.px-6 { padding-left: 1.5rem; padding-right: 1.5rem; }
.px-8 { padding-left: 2rem; padding-right: 2rem; }
.py-0\.5 { padding-top: 0.125rem; padding-bottom: 0.125rem; }
.py-1 { padding-top: 0.25rem; padding-bottom: 0.25rem; }
.py-1\.5 { padding-top: 0.375rem; padding-bottom: 0.375rem; }
.py-2 { padding-top: 0.5rem; padding-bottom: 0.5rem; }
.py-2\.5 { padding-top: 0.625rem; padding-bottom: 0.625rem; }
.py-3 { padding-top: 0.75rem; padding-bottom: 0.75rem; }
.py-4 { padding-top: 1rem; padding-bottom: 1rem; }
.py-6 { padding-top: 1.5rem; padding-bottom: 1.5rem; }

.space-y-4 > * + * { margin-top: 1rem; }
.space-y-6 > * + * { margin-top: 1.5rem; }
.space-y-8 > * + * { margin-top: 2rem; }
.space-x-4 > * + * { margin-left: 1rem; }
.space-x-6 > * + * { margin-left: 1.5rem; }

.divide-y > * + * { border-top: 1px solid #eef0f4; }
.divide-slate-100 > * + * { border-color: #f1f5f9; }

.bg-white { background-color: #ffffff; }
.bg-blue-600 { background-color: #2563eb; }
.bg-blue-50 { background-color: #eff6ff; }
.bg-red-600 { background-color: #dc2626; }
.bg-red-500 { background-color: #ef4444; }
.bg-red-50 { background-color: #fef2f2; }
.bg-green-600 { background-color: #16a34a; }
.bg-green-500 { background-color: #22c55e; }
.bg-green-50 { background-color: #f0fdf4; }
.bg-orange-600 { background-color: #ea580c; }
.bg-orange-500 { background-color: #f97316; }
.bg-orange-50 { background-color: #fff7ed; }
.bg-purple-600 { background-color: #9333ea; }
.bg-purple-50 { background-color: #faf5ff; }
.bg-indigo-600 { background-color: #4f46e5; }
.bg-indigo-50 { background-color: #eef2ff; }
.bg-emerald-500 { background-color: #10b981; }
.bg-emerald-50 { background-color: #ecfdf5; }
.bg-rose-500 { background-color: #f43f5e; }
.bg-rose-50 { background-color: #fff1f2; }
.bg-slate-50 { background-color: #f8fafc; }
.bg-slate-100 { background-color: #f1f5f9; }
.bg-slate-900\/60 { background-color: rgba(15, 23, 42, 0.6); }

.text-white { color: #ffffff; }
.text-blue-600 { color: #2563eb; }
.text-red-600 { color: #dc2626; }
.text-green-600 { color: #16a34a; }
.text-orange-600 { color: #ea580c; }
.text-purple-600 { color: #9333ea; }
.text-indigo-600 { color: #4f46e5; }
.text-emerald-600 { color: #059669; }
.text-amber-600 { color: #d97706; }
.text-rose-600 { color: #e11d48; }
.text-slate-900 { color: #0f172a; }
.text-slate-600 { color: #475569; }
.text-slate-500 { color: #64748b; }
.text-slate-400 { color: #94a3b8; }

.font-bold { font-weight: 700; }
.font-black { font-weight: 900; }
.font-extrabold { font-weight: 800; }
.font-medium { font-weight: 500; }
.uppercase { text-transform: uppercase; }
.italic { font-style: italic; }
.tracking-tight { letter-spacing: -0.025em; }
.tracking-tighter { letter-spacing: -0.05em; }
.tracking-widest { letter-spacing: 0.1em; }
.tracking-wider { letter-spacing: 0.05em; }

.rounded-lg { border-radius: 0.5rem; }
.rounded-xl { border-radius: 0.75rem; }
.rounded-2xl { border-radius: 1rem; }
.rounded-3xl { border-radius: 1.5rem; }
.rounded-full { border-radius: 9999px; }

.border { border: 1px solid #eef0f4; }
.border-b { border-bottom: 1px solid #eef0f4; }
.border-t { border-top: 1px solid #eef0f4; }
.border-l-4 { border-left-width: 4px; }
.border-slate-100 { border-color: #f1f5f9; }
.border-slate-200 { border-color: #e2e8f0; }

.shadow-sm { box-shadow: 0 1px 2px 0 rgba(0, 0, 0, 0.05); }
.shadow-md { box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06); }
.shadow-lg { box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05); }
.shadow-2xl { box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25); }
.shadow-slate-200\/50 { box-shadow: 0 4px 6px -1px rgba(226, 232, 240, 0.5); }

.overflow-hidden { overflow: hidden; }
.relative { position: relative; }
.absolute { position: absolute; }
.fixed { position: fixed; }
.inset-0 { top: 0; right: 0; bottom: 0; left: 0; }
.z-50 { z-index: 50; }
.backdrop-blur-sm { backdrop-filter: blur(4px); }

.animate-spin { animation: spin 1s linear infinite; }
@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }

.active\:scale-95:active { transform: scale(0.95); }
.transition-all { transition-property: all; transition-timing-function: cubic-bezier(0.4, 0, 0.2, 1); transition-duration: 150ms; }
.duration-1000 { transition-duration: 1000ms; }

.btn-primary-modal {
    background: #2563eb;
    color: white;
    padding: 0.75rem 1.5rem;
    border-radius: 0.75rem;
    font-weight: 700;
    border: none;
    cursor: pointer;
    transition: all 0.2s;
}
.btn-primary-modal:hover { background: #1d4ed8; }

.btn-secondary {
    background: #f1f5f9;
    color: #475569;
    padding: 0.75rem 1.5rem;
    border-radius: 0.75rem;
    font-weight: 700;
    border: none;
    cursor: pointer;
    transition: all 0.2s;
}
.btn-secondary:hover { background: #e2e8f0; }

.grid-cols-3 { grid-template-columns: repeat(3, minmax(0, 1fr)); }
.grid-cols-4 { grid-template-columns: repeat(4, minmax(0, 1fr)); }

.w-full { width: 100%; }
.w-12 { width: 3rem; }
.w-1\.5 { width: 0.375rem; }
.w-8 { width: 2rem; }
.h-12 { height: 3rem; }
.h-10 { height: 2.5rem; }
.h-8 { height: 2rem; }
.h-1\.5 { height: 0.375rem; }
.h-\[42px\] { height: 42px; }
.w-1\/3 { width: 33.333333%; }
.min-w-0 { min-width: 0; }
.min-w-\[140px\] { min-width: 140px; }
.min-w-\[200px\] { min-width: 200px; }
.min-w-\[300px\] { min-width: 300px; }
.max-w-md { max-width: 28rem; }
.max-w-\[400px\] { max-width: 400px; }

.mb-0\.5 { margin-bottom: 0.125rem; }
.mx-auto { margin-left: auto; margin-right: auto; }

.py-10 { padding-top: 2.5rem; padding-bottom: 2.5rem; }
.py-12 { padding-top: 3rem; padding-bottom: 3rem; }
.py-20 { padding-top: 5rem; padding-bottom: 5rem; }
.pl-10 { padding-left: 2.5rem; }
.pr-3 { padding-right: 0.75rem; }
.pr-4 { padding-right: 1rem; }

.bg-emerald-600 { background-color: #059669; }
.bg-rose-600 { background-color: #e11d48; }
.bg-slate-50\/50 { background-color: rgba(248, 250, 252, 0.5); }

.text-white { color: #ffffff !important; }
.text-blue-900 { color: #1e3a8a; }
.text-emerald-900 { color: #064e3b; }
.text-rose-900 { color: #881337; }
.text-amber-600 { color: #d97706; }

.leading-tight { line-height: 1.25; }
.leading-none { line-height: 1; }

.border-blue-100\/50 { border-color: rgba(219, 234, 254, 0.5); }
.border-emerald-100\/50 { border-color: rgba(209, 250, 229, 0.5); }
.border-rose-100\/50 { border-color: rgba(255, 228, 230, 0.5); }

.shadow-blue-200 { box-shadow: 0 10px 15px -3px rgba(191, 219, 254, 0.5); }
.shadow-emerald-200 { box-shadow: 0 10px 15px -3px rgba(167, 243, 208, 0.5); }
.shadow-rose-200 { box-shadow: 0 10px 15px -3px rgba(254, 205, 211, 0.5); }

.top-1\/2 { top: 50%; }
.left-3 { left: 0.75rem; }
.-translate-y-1\/2 { transform: translateY(-50%); }

.overflow-x-auto { overflow-x: auto; }

/* Custom Household Components */
.stat-card {
    background: white;
    padding: 1.5rem 2rem;
    border-radius: 1.25rem;
    border: 1px solid #eef0f4;
    box-shadow: 0 4px 6px -1px rgba(0,0,0,0.02), 0 2px 4px -1px rgba(0,0,0,0.01);
}

.statement-card {
    background: white;
    border-radius: 1.5rem;
    border: 1px solid #eef0f4;
    box-shadow: 0 4px 6px -1px rgba(0,0,0,0.02), 0 2px 4px -1px rgba(0,0,0,0.01);
}

.filter-card {
    background: white;
    padding: 1.5rem 2rem;
    border-radius: 1.5rem;
    border: 1px solid #eef0f4;
}

.balance-card {
    padding: 1.5rem;
    border-radius: 1.25rem;
}

.balance-label {
    font-size: 0.75rem;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    margin-bottom: 0.5rem;
}

.balance-value {
    font-size: 1.5rem;
    font-weight: 900;
    letter-spacing: -0.025em;
}

.filter-label {
    display: block;
    font-size: 0.65rem;
    font-weight: 900;
    color: #94a3b8;
    text-transform: uppercase;
    letter-spacing: 0.1em;
    margin-bottom: 0.5rem;
}

.search-input, .type-select {
    background: #f8fafc;
    border: 1px solid #e2e8f0;
    border-radius: 1rem;
    color: #0f172a;
    font-size: 0.875rem;
    font-weight: 600;
    outline: none;
    transition: all 0.2s;
    height: 42px; /* Standardize height */
}

.search-input:focus, .type-select:focus {
    background: white;
    border-color: #3b82f6;
    box-shadow: 0 0 0 4px rgba(59, 130, 246, 0.1);
}

.table-container {
    background: white;
    border-radius: 1.5rem;
    border: 1px solid #eef0f4;
    overflow: hidden;
    margin-top: 1.5rem;
}

.data-table {
    width: 100%;
    border-collapse: collapse;
}

.data-table th {
    background: #f8fafc;
    padding: 1rem 1.5rem;
    text-align: left;
    font-size: 0.65rem;
    font-weight: 900;
    color: #94a3b8;
    text-transform: uppercase;
    letter-spacing: 0.1em;
    border-bottom: 1px solid #eef0f4;
}

.data-table td {
    padding: 1.25rem 1.5rem;
    border-bottom: 1px solid #f1f5f9;
}

.data-table tr:last-child td {
    border-bottom: none;
}

.text-right { text-align: right !important; }
.text-center { text-align: center !important; }
.text-left { text-align: left !important; }
.text-\[10px\] { font-size: 10px; }
.text-xs { font-size: 0.75rem; }
.text-sm { font-size: 0.875rem; }
.text-base { font-size: 1rem; }
.text-lg { font-size: 1.125rem; }
.text-2xl { font-size: 1.5rem; }
.text-3xl { font-size: 1.875rem; }
.text-4xl { font-size: 2.25rem; }

/*
  По ТЗ 03.09.2026: единый дизайн action-кнопок в таблицах — как в 2 разделе
  КПС (KpsPersonnelView). До этого в каждой view был свой scoped-набор с
  расхождениями в цветах/размерах. Здесь — единственный источник правды.
  Локальные `.actions__btn--*` в отдельных view теперь можно постепенно
  удалять — глобальные значения продолжат работать.
*/
.actions {
  display: inline-flex;
  align-items: center;
  gap: 10px;
  justify-content: flex-end;
  width: 100%;
}
.actions__btn {
  width: 30px;
  height: 30px;
  padding: 6px;
  border-radius: 8px;
  border: 0;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-family: Arial, Helvetica, sans-serif;
  transition: opacity .15s ease, transform .05s ease;
}
.actions__btn:hover { opacity: .9; }
.actions__btn:active { transform: scale(.96); }
.actions__btn:disabled { opacity: .5; cursor: not-allowed; }
.actions__icon { width: 16px; height: 16px; display: block; }
.actions__label { color: #fff; font-family: Arial, Helvetica, sans-serif; line-height: 1; }

.actions__btn--edit    { background: #2F78EE; }
.actions__btn--accept  { background: #2A8A1B; }
.actions__btn--decline { background: #D81717; }
.actions__btn--archive { background: #FFC600; }
.actions__btn--delete  { background: #D81717; }
.actions__btn--card    { background: #6366F1; color: #fff; width: auto; padding: 6px 10px; font-size: 12px; font-weight: 600; }

/*
  По ТЗ 03.09.2026: единый стиль вспомогательных кнопок в таблицах
  (Добавить/Изменить/Выбрать участок и т.п.). Совпадает по формам с
  ActionButton --sm — те же радиус, шрифт, hover. `.btn-small.btn-green`
  = ActionButton зелёного цвета.
*/
.btn-small {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  height: 36px;
  min-width: 40px;
  padding: 0 14px;
  border-radius: 10px;
  border: 1px solid #2274D4;
  background: #FFFFFF;
  color: #2274D4;
  cursor: pointer;
  font-family: inherit;
  font-weight: 600;
  font-size: 14px;
  line-height: 20px;
  transition: opacity .15s ease, box-shadow .2s ease, transform .05s ease;
}
.btn-small:hover { box-shadow: 0 2px 8px rgba(0,0,0,.08); }
.btn-small:active { transform: translateY(1px); }
.btn-small:disabled { opacity: .55; cursor: not-allowed; box-shadow: none; }
.btn-small.btn-green {
  background: #2A8A1B;
  color: #FFFFFF;
  border-color: #2A8A1B;
}
.btn-small.btn-blue {
  background: #2F78EE;
  color: #FFFFFF;
  border-color: #2F78EE;
}
.btn-small.btn-red {
  background: #D81717;
  color: #FFFFFF;
  border-color: #D81717;
}

/*
  Тулбарные кнопки (`.btn` / `.btn--primary/secondary/danger`) —
  унифицированы с DailyScheduleView. Используются в DailyScheduleView,
  WorkloadView, MapSchemeView, KioskSurveyView.
*/
.btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 10px 18px;
  border-radius: 10px;
  border: 1px solid transparent;
  cursor: pointer;
  font-family: inherit;
  font-weight: 600;
  font-size: 14px;
  line-height: 22px;
  transition: opacity .15s ease, box-shadow .2s ease, transform .05s ease;
}
.btn:hover { box-shadow: 0 2px 8px rgba(0,0,0,.08); }
.btn:active { transform: translateY(1px); }
.btn:disabled { opacity: .55; cursor: not-allowed; box-shadow: none; }
.btn--primary { background: #2F78EE; color: #fff; border-color: #2F78EE; }
.btn--primary:hover { background: #1D4ED8; }
.btn--secondary { background: #fff; color: #374151; border-color: #D1D5DB; }
.btn--secondary:hover { background: #F3F4F6; }
.btn--danger { background: #D81717; color: #fff; border-color: #D81717; }
</style>
